import { useState, useEffect } from 'react'
import {
  Stack,
  Button,
  Modal,
  TextInput,
  NumberInput,
  Group,
  Badge,
  ActionIcon,
  Table,
  Text,
} from '@mantine/core'
import { IconTrash, IconEdit } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { ResponsiveTitle } from '../ResponsiveTitle/ResponsiveTitle'
import { supabase } from '../../lib/supabase'
import type { AdditionalGuest } from '../../lib/types'
import styles from './AdditionalGuestForm.module.css'

interface AdditionalGuestFormProps {
  guestId: string
  onUpdate?: () => void
}

export function AdditionalGuestForm({ guestId, onUpdate }: AdditionalGuestFormProps) {
  const [additionalGuests, setAdditionalGuests] = useState<AdditionalGuest[]>([])
  const [, setLoading] = useState(true)

  const [plusOneModalOpen, setPlusOneModalOpen] = useState(false)
  const [childModalOpen, setChildModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedGuest, setSelectedGuest] = useState<AdditionalGuest | null>(null)
  const [editingGuest, setEditingGuest] = useState<AdditionalGuest | null>(null)

  const [plusOneName, setPlusOneName] = useState('')
  const [childName, setChildName] = useState('')
  const [childAge, setChildAge] = useState<number | undefined>(undefined)

  const [adding, setAdding] = useState(false)

  useEffect(() => {
    loadAdditionalGuests()
  }, [guestId])

  const loadAdditionalGuests = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('additional_guests')
      .select('*')
      .eq('guest_id', guestId)
      .order('added_date', { ascending: true })

    if (!error && data) {
      setAdditionalGuests(data)
    }
    setLoading(false)
  }

  const handleAddPlusOne = async () => {
    if (!plusOneName.trim()) return

    setAdding(true)
    
    if (editingGuest) {
      // Update existing guest
      const { error } = await supabase
        .from('additional_guests')
        .update({ name: plusOneName.trim() })
        .eq('id', editingGuest.id)

      if (!error) {
        notifications.show({
          title: 'Erfolg',
          message: 'Begleitung wurde aktualisiert',
          color: 'green',
        })
        setPlusOneName('')
        setEditingGuest(null)
        setPlusOneModalOpen(false)
        loadAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Begleitung konnte nicht aktualisiert werden',
          color: 'red',
        })
      }
    } else {
      // Add new guest
      const { error } = await supabase.from('additional_guests').insert({
        guest_id: guestId,
        type: 'plus_one',
        name: plusOneName.trim(),
        age: null,
        added_by: 'admin',
      })

      if (!error) {
        notifications.show({
          title: 'Erfolg',
          message: 'Begleitung wurde hinzugefügt',
          color: 'green',
        })
        setPlusOneName('')
        setPlusOneModalOpen(false)
        loadAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Begleitung konnte nicht hinzugefügt werden',
          color: 'red',
        })
      }
    }

    setAdding(false)
  }

  const handleAddChild = async () => {
    if (!childName.trim() || childAge === undefined) return

    // Validate age
    if (childAge < 0 || childAge > 17) {
      notifications.show({
        title: 'Fehler',
        message: 'Alter muss zwischen 0 und 17 liegen',
        color: 'red',
      })
      return
    }

    setAdding(true)

    if (editingGuest) {
      // Update existing guest
      const { error } = await supabase
        .from('additional_guests')
        .update({ name: childName.trim(), age: childAge })
        .eq('id', editingGuest.id)

      if (!error) {
        notifications.show({
          title: 'Erfolg',
          message: 'Kind wurde aktualisiert',
          color: 'green',
        })
        setChildName('')
        setChildAge(undefined)
        setEditingGuest(null)
        setChildModalOpen(false)
        loadAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Kind konnte nicht aktualisiert werden',
          color: 'red',
        })
      }
    } else {
      // Add new guest
      const { error } = await supabase.from('additional_guests').insert({
        guest_id: guestId,
        type: 'child',
        name: childName.trim(),
        age: childAge,
        added_by: 'admin',
      })

      if (!error) {
        notifications.show({
          title: 'Erfolg',
          message: 'Kind wurde hinzugefügt',
          color: 'green',
        })
        setChildName('')
        setChildAge(undefined)
        setChildModalOpen(false)
        loadAdditionalGuests()
        onUpdate?.()
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Kind konnte nicht hinzugefügt werden',
          color: 'red',
        })
      }
    }

    setAdding(false)
  }

  const handleDelete = async () => {
    if (!selectedGuest) return

    const { error } = await supabase
      .from('additional_guests')
      .delete()
      .eq('id', selectedGuest.id)

    if (!error) {
      notifications.show({
        title: 'Erfolg',
        message: 'Zusätzlicher Gast wurde entfernt',
        color: 'green',
      })
      setDeleteModalOpen(false)
      setSelectedGuest(null)
      loadAdditionalGuests()
      onUpdate?.()
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Zusätzlicher Gast konnte nicht entfernt werden',
        color: 'red',
      })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <ResponsiveTitle order={3} className={styles.title}>
          Zusätzliche Gäste
        </ResponsiveTitle>
        <Group gap="xs">
          <Button size="sm" variant="light" onClick={() => setPlusOneModalOpen(true)}>
            +1 hinzufügen
          </Button>
          <Button size="sm" variant="light" onClick={() => setChildModalOpen(true)}>
            Kind hinzufügen
          </Button>
        </Group>
      </Group>

      {additionalGuests.length === 0 ? (
        <Text c="dimmed" ta="center" py="md">
          Keine zusätzlichen Gäste
        </Text>
      ) : (
        <Table className={styles.table}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Alter</Table.Th>
              <Table.Th>Hinzugefügt von</Table.Th>
              <Table.Th>Aktionen</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {additionalGuests.map((guest) => (
              <Table.Tr key={guest.id}>
                <Table.Td>{guest.name}</Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={guest.type === 'child' ? 'blue' : 'cyan'}>
                    {guest.type === 'child' ? 'Kind' : 'Begleitung'}
                  </Badge>
                </Table.Td>
                <Table.Td>{guest.age !== null ? guest.age : '-'}</Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={guest.added_by === 'admin' ? 'grape' : 'teal'}
                  >
                    {guest.added_by === 'admin' ? 'Admin' : 'Gast'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => {
                        setEditingGuest(guest)
                        if (guest.type === 'plus_one') {
                          setPlusOneName(guest.name)
                          setPlusOneModalOpen(true)
                        } else {
                          setChildName(guest.name)
                          setChildAge(guest.age || undefined)
                          setChildModalOpen(true)
                        }
                      }}
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        setSelectedGuest(guest)
                        setDeleteModalOpen(true)
                      }}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Plus One Modal */}
      <Modal
        opened={plusOneModalOpen}
        onClose={() => {
          setPlusOneModalOpen(false)
          setPlusOneName('')
          setEditingGuest(null)
        }}
        title={editingGuest ? 'Begleitung bearbeiten' : '+1 hinzufügen'}
        centered
      >
        <form onSubmit={(e) => {
          e.preventDefault()
          if (plusOneName.trim() && !adding) {
            handleAddPlusOne()
          }
        }}>
          <Stack>
            <TextInput
              label="Name der Begleitung"
              placeholder="Name eingeben"
              value={plusOneName}
              onChange={(e) => setPlusOneName(e.currentTarget.value)}
              required
              autoFocus
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" type="button" onClick={() => {
                setPlusOneModalOpen(false)
                setPlusOneName('')
                setEditingGuest(null)
              }}>
                Abbrechen
              </Button>
              <Button type="submit" loading={adding} disabled={!plusOneName.trim()}>
                {editingGuest ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Child Modal */}
      <Modal
        opened={childModalOpen}
        onClose={() => {
          setChildModalOpen(false)
          setChildName('')
          setChildAge(undefined)
          setEditingGuest(null)
        }}
        title={editingGuest ? 'Kind bearbeiten' : 'Kind hinzufügen'}
        centered
      >
        <form onSubmit={(e) => {
          e.preventDefault()
          if (childName.trim() && childAge !== undefined && !adding) {
            handleAddChild()
          }
        }}>
          <Stack>
            <TextInput
              label="Name des Kindes"
              placeholder="Name eingeben"
              value={childName}
              onChange={(e) => setChildName(e.currentTarget.value)}
              required
              autoFocus
            />
            <NumberInput
              label="Alter"
              placeholder="Alter eingeben"
              value={childAge}
              onChange={(val) => setChildAge(val as number)}
              min={0}
              max={17}
              required
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" type="button" onClick={() => {
                setChildModalOpen(false)
                setChildName('')
                setChildAge(undefined)
                setEditingGuest(null)
              }}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                loading={adding}
                disabled={!childName.trim() || childAge === undefined}
              >
                {editingGuest ? 'Aktualisieren' : 'Hinzufügen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Gast entfernen"
        centered
      >
        <Stack>
          <Text>
            Möchten Sie diesen zusätzlichen Gast wirklich entfernen?
            <br />
            <strong>{selectedGuest?.name}</strong>
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>
              Abbrechen
            </Button>
            <Button color="red" onClick={handleDelete}>
              Entfernen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
